import React, { useState, useEffect, useRef, useLayoutEffect, useMemo, useCallback } from 'react';

import { useQuery } from 'react-admin';
import { useField } from 'react-final-form';
import classNames from 'classnames';

import makeStyles from '@material-ui/core/styles/makeStyles';
import ToolButton from 'components/buttons/ToolButton';
import { Skeleton } from 'components/layout/Skeleton';
import ShippingPresetDialog from './ShippingPresetDialog';

import { useWindowSize } from '../utils';
import { isImperialUnitMeasurement } from 'services/helpers/unitsMeasurement';
import { convertGmToKgGm, convertOzToLbOz } from 'services/helpers/convertWeight';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    width: '100%',
    padding: theme.spacing(3, 0, 1.5, 0),
    justifyContent: 'flex-start',
  },

  button: {
    marginRight: theme.spacing(1),
    width: 'fit-content',
    maxWidth: '220px',
    minWidth: '100px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    justifyContent: 'flex-start',

    '& span': {
      display: 'inline-block',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      overflowWrap: 'break-word',
      whiteSpace: 'nowrap',
      textTransform: 'none',
      fontSize: 13,
    },
  },

  active: {
    color: `${theme.palette.primary.main} !important`,
    padding: theme.spacing(0.5, 2),
  },
  skeletonContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    overflow: 'hidden',
  },
  skeleton: {
    minWidth: '100px !important',
    height: '24px !important',
  },
}));

interface Props {
  name: string;
  country?: string;
  excludeHazmat?: boolean;
  excludeAlcohol?: boolean;
  carriers?: string[];
}

const ShippingPresetChooser = ({
  name,
  country,
  excludeHazmat = false,
  excludeAlcohol = false,
  carriers = [],
}: Props) => {
  const classes = useStyles();

  const refContainer = useRef(null);
  const size = useWindowSize(refContainer);

  const isImperial = isImperialUnitMeasurement();

  const { loading, data } = useQuery({
    type: 'get',
    resource: 'popularShippingPresets',
    payload: { amount: 100, dstCountry: country },
  });

  const {
    input: { value, onChange },
  } = useField(name);

  const customPreset = useMemo(
    () => ({
      id: null,
      name: 'Custom',
      type: 'PACKAGE',
      size: {
        width: null,
        height: null,
        length: null,
      },
      weight: {
        lb: null,
        oz: null,
        kg: null,
        gm: null,
      },
      flatRate: false,
      provider: null,
    }),
    []
  );

  const [rawPresets, setRawPresets] = useState<any[]>([]);
  const [initialPresets, setInitialPresets] = useState<any[]>([]);
  const [presets, setPresets] = useState<any[]>([]);
  const [quickPresets, setQuickPresets] = useState<any[]>([customPreset]);

  const loadPresets = useCallback(
    (presetsList) =>
      presetsList.map((item) => {
        const { weightLb, weightOz } = convertOzToLbOz(item.weightOz);
        const { weightKg, weightGm } = convertGmToKgGm(item.weightGm);

        return {
          id: item.id,
          name: item.name,
          type: item.packageType.code,
          size: {
            width: item.width,
            height: item.height,
            length: item.length,
          },
          flatRate: item.packageType.flatRate,
          weight: isImperial
            ? {
                lb: weightLb,
                oz: weightOz,
              }
            : {
                kg: weightKg,
                gm: weightGm,
              },
          provider: item.packageType.provider,
        };
      }),
    [isImperial]
  );

  const carriersSignature = useMemo(
    () => JSON.stringify(carriers && carriers.length ? [...new Set(carriers)] : []),
    [carriers]
  );

  const filterPresets = useCallback(
    (presetList: any[]) => {
      const carriersFilter =
        carriersSignature && carriersSignature !== '[]'
          ? new Set(JSON.parse(carriersSignature))
          : null;

      return presetList.filter(
        (preset) =>
          (excludeHazmat ? !preset.type.includes('HAZMAT') : true) &&
          (excludeAlcohol ? !preset.type.includes('ALCOHOL') : true) &&
          (carriersFilter ? carriersFilter.has(preset.provider) || preset.provider === null : true)
      );
    },
    [excludeHazmat, excludeAlcohol, carriersSignature]
  );

  const lastDataSignature = useRef('');

  useEffect(() => {
    if (loading || !Array.isArray(data) || !data.length) return;

    const signature = data.map((preset) => preset.id).join('|');
    if (signature === lastDataSignature.current) return;

    lastDataSignature.current = signature;
    const transformed = loadPresets(data);
    setRawPresets(transformed);
  }, [loading, data, loadPresets]);

  useEffect(() => {
    if (!rawPresets.length) {
      setInitialPresets([]);
      setPresets([]);
      return;
    }

    const filtered = filterPresets(rawPresets);
    setInitialPresets(filtered);
    setPresets(filtered);
  }, [rawPresets, filterPresets]);

  useLayoutEffect(() => {
    if (initialPresets.length) {
      const middleButtonSize = 160;

      // Width using only for the initial render
      const width = refContainer?.current?.offsetWidth ?? 0;

      const amountQuickPresets = Math.max(
        Math.floor((size || width - middleButtonSize) / middleButtonSize),
        2
      );

      const newQuickPresets = initialPresets.slice(0, amountQuickPresets);
      const existingPreset = initialPresets.find((preset) => preset.id === value.id);
      if (
        value?.id &&
        !newQuickPresets.some((preset) => preset.id === value.id) &&
        existingPreset
      ) {
        setQuickPresets([
          customPreset,
          existingPreset,
          ...newQuickPresets.slice(0, amountQuickPresets - 1),
        ]);
      } else {
        if (!existingPreset) value.id = null;
        setQuickPresets([customPreset, ...newQuickPresets]);
      }
    }
  }, [customPreset, initialPresets, size, value]);

  const handleChange = (selectedPreset) => () => {
    onChange({
      ...value,
      ...selectedPreset,
    });
  };

  const isQuickPreset = () => quickPresets.some((preset) => preset.id === value.id);

  if (!data || !data.length) return null;

  return (
    <>
      <div className={classes.container} ref={refContainer}>
        {loading && (
          <div className={classes.skeletonContainer}>
            <Skeleton classes={{ root: classes.skeleton }} variant="rect" />
            <Skeleton classes={{ root: classes.skeleton }} variant="rect" />
            <Skeleton classes={{ root: classes.skeleton }} variant="rect" />
            <Skeleton classes={{ root: classes.skeleton }} variant="rect" />
            <Skeleton classes={{ root: classes.skeleton }} variant="rect" />
            <Skeleton classes={{ root: classes.skeleton }} variant="rect" />
            <Skeleton classes={{ root: classes.skeleton }} variant="rect" />
            <Skeleton classes={{ root: classes.skeleton }} variant="rect" />
            <Skeleton classes={{ root: classes.skeleton }} variant="rect" />
            <Skeleton classes={{ root: classes.skeleton }} variant="rect" />
            <Skeleton classes={{ root: classes.skeleton }} variant="rect" />
          </div>
        )}
        {!loading && (
          <>
            {quickPresets.map((preset) => {
              const isActive = preset.id === value.id;
              return (
                <ToolButton
                  key={preset.id ?? preset.name}
                  onClick={handleChange(preset)}
                  className={classNames(classes.button, isActive && classes.active)}>
                  {preset.name}
                </ToolButton>
              );
            })}
            {quickPresets.length >= 3 && (
              <ShippingPresetDialog
                initialPresets={initialPresets}
                presets={presets}
                setPresets={setPresets}
                onChange={onChange}
                fieldValue={value}
                isQuickPreset={isQuickPreset}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ShippingPresetChooser;
